import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogClose } from '../components/ui/dialog';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';
import AIKeyRequiredDialog from '../components/AIKeyRequiredDialog';
import { 
  formatCurrency, formatNumber, formatPercentage, formatDate,
  exportToPDF, exportToExcel, calculateTrend, getWeekNumber
} from '../utils/helpers';
import {
  Plus, CalendarIcon, TrendingUp, TrendingDown, Users, 
  Receipt, Download, FileSpreadsheet, FileText, Filter, Upload, Loader2, Trash2, CreditCard, Wallet, ArrowUpRight, ArrowDownRight, BarChart3, Target, Settings2, PartyPopper, Package
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, 
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

const OmzetVerkoopPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [channelDialogOpen, setChannelDialogOpen] = useState(false);
  const [newChannel, setNewChannel] = useState('');
  const [kassaReports, setKassaReports] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [categoryTrendsData, setCategoryTrendsData] = useState(null);
  const [periodStats, setPeriodStats] = useState(null);
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetType, setTargetType] = useState('month'); // year, month, week
  const [targetAmount, setTargetAmount] = useState('');
  const [eventRevenue, setEventRevenue] = useState([]);
  const [arrangementRevenue, setArrangementRevenue] = useState([]);
  const fileInputRef = useRef(null);
  const [showAIKeyDialog, setShowAIKeyDialog] = useState(false);
  
  // Period selection state
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedWeek, setSelectedWeek] = useState(Math.ceil((currentDate - new Date(currentDate.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000)));
  
  const [filters, setFilters] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)),
    endDate: new Date(),
    location: 'all',
    period: 'dag'
  });
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    location_id: '',
    channel: '',
    period_part: '',
    guests: '',
    transactions: '',
    // New period fields
    entry_type: 'dag', // dag, week, maand, jaar
    end_date: '',
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    week: Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000))
  });

  // Quick add function for period cards
  const openQuickAdd = (type) => {
    const now = new Date();
    const weekNum = Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000));
    setFormData({
      ...formData,
      entry_type: type,
      year: type === 'jaar' ? selectedYear : (type === 'maand' ? selectedYear : now.getFullYear()),
      month: type === 'maand' ? selectedMonth : now.getMonth() + 1,
      week: type === 'week' ? selectedWeek : weekNum,
      amount: '',
      guests: '',
      transactions: ''
    });
    setDialogOpen(true);
  };

  useEffect(() => {
    fetchData();
  }, [filters.startDate, filters.endDate, filters.location]);

  useEffect(() => {
    fetchPeriodStats();
  }, [selectedYear, selectedMonth, selectedWeek]);

  const fetchPeriodStats = async () => {
    try {
      const res = await api.get('/revenue/period-stats', {
        params: { year: selectedYear, month: selectedMonth, week: selectedWeek }
      });
      setPeriodStats(res.data);
      
      // Check for goal alerts in background
      api.get('/revenue/targets/check-alerts').catch(() => {});
    } catch (error) {
      console.error('Error fetching period stats:', error);
    }
  };

  const fetchData = async () => {
    try {
      const params = {
        start_date: filters.startDate.toISOString().split('T')[0],
        end_date: filters.endDate.toISOString().split('T')[0]
      };
      if (filters.location !== 'all') params.location_id = filters.location;

      const [entriesRes, locationsRes, channelsRes, reportsRes, trendsRes, eventRevenueRes, arrRevenueRes] = await Promise.all([
        api.get('/revenue', { params }),
        api.get('/locations'),
        api.get('/channels'),
        api.get('/revenue/reports'),
        api.get('/revenue/category-trends', { 
          params: { 
            current_start: params.start_date, 
            current_end: params.end_date 
          } 
        }).catch(() => ({ data: null })),
        api.get('/event-revenue').catch(() => ({ data: [] })),
        api.get('/arrangement-revenue').catch(() => ({ data: [] }))
      ]);
      
      setEntries(entriesRes.data);
      setLocations(locationsRes.data);
      setChannels(channelsRes.data);
      setKassaReports(reportsRes.data);
      setCategoryTrendsData(trendsRes.data);
      
      // Filter event revenue by date range
      const filteredEventRevenue = (eventRevenueRes.data || []).filter(er => {
        const revDate = new Date(er.date || er.created_at);
        return revDate >= filters.startDate && revDate <= filters.endDate;
      });
      setEventRevenue(filteredEventRevenue);
      
      // Filter arrangement revenue by date range
      const filteredArrRevenue = (arrRevenueRes.data || []).filter(ar => {
        const revDate = new Date(ar.date || ar.created_at);
        return revDate >= filters.startDate && revDate <= filters.endDate;
      });
      setArrangementRevenue(filteredArrRevenue);
    } catch (error) {
      toast.error('Fout bij laden data');
    } finally {
      setLoading(false);
    }
  };

  // Dutch month names
  const monthNames = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni', 
                      'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'];

  // Handle setting revenue target
  const handleSetTarget = async () => {
    if (!targetAmount || parseFloat(targetAmount) <= 0) {
      toast.error('Voer een geldig doelbedrag in');
      return;
    }

    try {
      const payload = {
        period_type: targetType,
        year: selectedYear,
        month: targetType === 'month' ? selectedMonth : null,
        week: targetType === 'week' ? selectedWeek : null,
        target_amount: parseFloat(targetAmount),
        description: `Omzet doelstelling ${targetType === 'year' ? selectedYear : targetType === 'month' ? monthNames[selectedMonth - 1] + ' ' + selectedYear : 'Week ' + selectedWeek + ' ' + selectedYear}`,
        create_goal: true
      };

      const res = await api.post('/revenue/targets', payload);
      
      if (res.data.success) {
        toast.success(res.data.updated 
          ? 'Omzet doelstelling bijgewerkt!' 
          : 'Omzet doelstelling aangemaakt en gekoppeld aan Doelen & Structuur!',
          { duration: 4000 }
        );
        setTargetDialogOpen(false);
        setTargetAmount('');
        fetchPeriodStats();
      }
    } catch (error) {
      toast.error('Fout bij opslaan doelstelling');
    }
  };

  // Open target dialog with pre-filled values
  const openTargetDialog = (type) => {
    setTargetType(type);
    // Pre-fill with existing target if available
    const existingTarget = periodStats?.[type]?.target;
    setTargetAmount(existingTarget ? String(existingTarget) : '');
    setTargetDialogOpen(true);
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.pdf')) {
      toast.error('Alleen PDF bestanden zijn toegestaan');
      return;
    }
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post('/revenue/upload-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        toast.success(res.data.message, { duration: 5000 });
        if (res.data.revenue_entries_created?.length > 0) {
          toast.info(`${res.data.revenue_entries_created.length} omzet entry(s) automatisch aangemaakt`, { duration: 4000 });
        }
        setSelectedReport(res.data.report);
        setReportDialogOpen(true);
        fetchData();
      } else {
        toast.error(res.data.error || 'Upload mislukt');
      }
    } catch (error) {
      if (error.response?.status === 412 || error.response?.data?.detail === 'NO_API_KEY') {
        setShowAIKeyDialog(true);
      } else {
        toast.error(error.response?.data?.detail || 'Fout bij uploaden PDF');
      }
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteReport = async (reportId) => {
    if (!window.confirm('Rapport verwijderen?')) return;
    try {
      await api.delete(`/revenue/reports/${reportId}`);
      toast.success('Rapport verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Calculate dates based on entry type
      let startDate, endDate, channelName;
      const year = formData.year;
      const month = formData.month;
      const week = formData.week;
      
      switch (formData.entry_type) {
        case 'jaar':
          startDate = `${year}-01-01`;
          endDate = `${year}-12-31`;
          channelName = formData.channel || `Jaaromzet ${year}`;
          break;
        case 'maand':
          const lastDay = new Date(year, month, 0).getDate();
          startDate = `${year}-${String(month).padStart(2, '0')}-01`;
          endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
          channelName = formData.channel || `Maandomzet ${monthNames[month - 1]} ${year}`;
          break;
        case 'week':
          // Calculate week start (Monday) and end (Sunday)
          const jan1 = new Date(year, 0, 1);
          const weekStart = new Date(jan1.getTime() + ((week - 1) * 7 - jan1.getDay() + 1) * 24 * 60 * 60 * 1000);
          const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
          startDate = weekStart.toISOString().split('T')[0];
          endDate = weekEnd.toISOString().split('T')[0];
          channelName = formData.channel || `Weekomzet W${week} ${year}`;
          break;
        default: // dag
          startDate = formData.date;
          endDate = null;
          channelName = formData.channel || null;
      }
      
      await api.post('/revenue', {
        date: startDate,
        period_end: endDate,
        amount: parseFloat(formData.amount) || 0,
        guests: parseInt(formData.guests) || 0,
        transactions: parseInt(formData.transactions) || 0,
        location_id: formData.location_id || null,
        channel: channelName,
        period_part: formData.period_part || null,
        source: formData.entry_type !== 'dag' ? `${formData.entry_type}_entry` : null
      });
      
      const typeLabels = { dag: 'Dag', week: 'Week', maand: 'Maand', jaar: 'Jaar' };
      toast.success(`${typeLabels[formData.entry_type]}omzet toegevoegd`);
      setDialogOpen(false);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        location_id: '',
        channel: '',
        period_part: '',
        guests: '',
        transactions: '',
        entry_type: 'dag',
        end_date: '',
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        week: Math.ceil((new Date() - new Date(new Date().getFullYear(), 0, 1)) / (7 * 24 * 60 * 60 * 1000))
      });
      fetchData();
      fetchPeriodStats();
    } catch (error) {
      toast.error('Fout bij opslaan');
    }
  };

  const handleAddChannel = async () => {
    if (!newChannel.trim()) return;
    try {
      await api.post('/channels', null, { params: { name: newChannel } });
      toast.success('Kanaal toegevoegd');
      setNewChannel('');
      setChannelDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Fout bij toevoegen kanaal');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze omzet wilt verwijderen?')) return;
    try {
      await api.delete(`/revenue/${id}`);
      toast.success('Omzet verwijderd');
      fetchData();
    } catch (error) {
      toast.error('Fout bij verwijderen');
    }
  };

  // Calculate totals and metrics
  const totalRevenue = entries.reduce((sum, e) => sum + (e.amount || 0), 0);
  const totalGuests = entries.reduce((sum, e) => sum + (e.guests || 0), 0);
  const totalTransactions = entries.reduce((sum, e) => sum + (e.transactions || 0), 0);
  const avgSpending = totalGuests > 0 ? totalRevenue / totalGuests : 0;
  
  // Event revenue calculations
  const totalEventRevenue = eventRevenue.reduce((sum, er) => sum + (er.amount || 0), 0);
  const totalEventGuests = eventRevenue.reduce((sum, er) => sum + (er.guests || 0), 0);
  
  // Group event revenue by event
  const eventRevenueByEvent = eventRevenue.reduce((acc, er) => {
    const eventName = er.event_name || 'Onbekend Event';
    if (!acc[eventName]) acc[eventName] = { amount: 0, guests: 0, items: [] };
    acc[eventName].amount += er.amount || 0;
    acc[eventName].guests += er.guests || 0;
    acc[eventName].items.push(er);
    return acc;
  }, {});
  
  // Arrangement revenue calculations
  const totalArrangementRevenue = arrangementRevenue.reduce((sum, ar) => sum + (ar.amount || 0), 0);
  const totalArrangementGuests = arrangementRevenue.reduce((sum, ar) => sum + (ar.guests || 0), 0);
  
  // Group arrangement revenue by arrangement
  const arrangementRevenueByArr = arrangementRevenue.reduce((acc, ar) => {
    const arrName = ar.arrangement_name || 'Onbekend Arrangement';
    if (!acc[arrName]) acc[arrName] = { amount: 0, guests: 0, items: [] };
    acc[arrName].amount += ar.amount || 0;
    acc[arrName].guests += ar.guests || 0;
    acc[arrName].items.push(ar);
    return acc;
  }, {});
  
  // Combined total revenue
  const combinedTotalRevenue = totalRevenue + totalEventRevenue + totalArrangementRevenue;

  // Group by date for chart
  const chartData = entries
    .reduce((acc, entry) => {
      const existing = acc.find(a => a.date === entry.date);
      if (existing) {
        existing.omzet += entry.amount || 0;
        existing.gasten += entry.guests || 0;
      } else {
        acc.push({ 
          date: entry.date, 
          omzet: entry.amount || 0,
          gasten: entry.guests || 0
        });
      }
      return acc;
    }, [])
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(d => ({
      ...d,
      label: format(new Date(d.date), 'dd MMM', { locale: nl })
    }));

  // Group by channel for pie chart
  const channelData = entries
    .filter(e => e.channel)
    .reduce((acc, entry) => {
      const existing = acc.find(a => a.name === entry.channel);
      if (existing) {
        existing.value += entry.amount || 0;
      } else {
        acc.push({ name: entry.channel, value: entry.amount || 0 });
      }
      return acc;
    }, [])
    .sort((a, b) => b.value - a.value);

  // Use API data for category trends, fallback to local calculation
  const categoryTrends = categoryTrendsData?.trends || [];
  const trendsSummary = categoryTrendsData?.summary || {};
  const trendsPeriod = categoryTrendsData?.period || {};

  // Top performing categories
  const topCategories = channelData.slice(0, 5);
  const totalCategoryRevenue = trendsSummary.totalCurrentRevenue || channelData.reduce((sum, c) => sum + c.value, 0);

  const COLORS = ['#C2410C', '#1A2E05', '#D97706', '#57534E', '#78716C', '#0891B2', '#7C3AED', '#DB2777'];

  const handleExportPDF = () => {
    const columns = [
      { header: 'Datum', key: 'date' },
      { header: 'Omzet', key: 'amountFormatted' },
      { header: 'Gasten', key: 'guests' },
      { header: 'Transacties', key: 'transactions' },
      { header: 'Kanaal', key: 'channel' }
    ];
    const data = entries.map(e => ({
      ...e,
      date: formatDate(e.date),
      amountFormatted: formatCurrency(e.amount)
    }));
    exportToPDF('Omzet & Verkoop', columns, data, 'omzet-verkoop');
  };

  const handleExportExcel = () => {
    const columns = [
      { header: 'Datum', key: 'date' },
      { header: 'Omzet', key: 'amount' },
      { header: 'Gasten', key: 'guests' },
      { header: 'Transacties', key: 'transactions' },
      { header: 'Kanaal', key: 'channel' },
      { header: 'Locatie', key: 'location_id' }
    ];
    exportToExcel('Omzet & Verkoop', columns, entries, 'omzet-verkoop');
  };

  return (
    <div className="p-6 md:p-8 space-y-6 animate-fade-in" data-testid="omzet-verkoop-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1C1917]" style={{ fontFamily: 'Manrope' }}>
            Omzet & Verkoop
          </h1>
          <p className="text-[#57534E] mt-1">
            Beheer en analyseer uw omzet per dag, week, maand en jaar
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* PDF Upload */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handlePdfUpload}
            accept=".pdf"
            className="hidden"
          />
          <Button 
            variant="outline" 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="border-[#C2410C] text-[#C2410C] hover:bg-[#C2410C]/10"
            data-testid="upload-pdf-btn"
          >
            {uploading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploaden...</>
            ) : (
              <><Upload className="w-4 h-4 mr-2" />Kassa PDF</>
            )}
          </Button>
          <Dialog open={channelDialogOpen} onOpenChange={setChannelDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="btn-secondary" data-testid="add-channel-btn">
                Kanaal toevoegen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nieuw kanaal toevoegen</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2">
                  <Label>Kanaal naam</Label>
                  <Input
                    value={newChannel}
                    onChange={(e) => setNewChannel(e.target.value)}
                    placeholder="bijv. Thuisbezorgd, Uber Eats"
                  />
                </div>
                <Button onClick={handleAddChannel} className="w-full btn-primary">
                  Toevoegen
                </Button>
              </div>
            </DialogContent>
          </Dialog>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="add-revenue-btn">
                <Plus className="w-4 h-4 mr-2" />
                Omzet toevoegen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Omzet invoeren</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                {/* Period Type Selection */}
                <div className="space-y-2">
                  <Label>Type invoer</Label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: 'dag', label: 'Dag' },
                      { value: 'week', label: 'Week' },
                      { value: 'maand', label: 'Maand' },
                      { value: 'jaar', label: 'Jaar' }
                    ].map(type => (
                      <Button
                        key={type.value}
                        type="button"
                        variant={formData.entry_type === type.value ? 'default' : 'outline'}
                        className={formData.entry_type === type.value ? 'bg-[#C2410C] hover:bg-[#C2410C]/90' : ''}
                        onClick={() => setFormData({ ...formData, entry_type: type.value })}
                        data-testid={`entry-type-${type.value}`}
                      >
                        {type.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Dynamic Date Fields based on entry type */}
                {formData.entry_type === 'dag' && (
                  <div className="space-y-2">
                    <Label>Datum</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="revenue-date-input"
                    />
                  </div>
                )}

                {formData.entry_type === 'week' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Jaar</Label>
                      <Select
                        value={String(formData.year)}
                        onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1].map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Weeknummer</Label>
                      <Select
                        value={String(formData.week)}
                        onValueChange={(v) => setFormData({ ...formData, week: parseInt(v) })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 52 }, (_, i) => i + 1).map(w => (
                            <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {formData.entry_type === 'maand' && (
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Jaar</Label>
                      <Select
                        value={String(formData.year)}
                        onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1].map(y => (
                            <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Maand</Label>
                      <Select
                        value={String(formData.month)}
                        onValueChange={(v) => setFormData({ ...formData, month: parseInt(v) })}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {monthNames.map((m, i) => (
                            <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {formData.entry_type === 'jaar' && (
                  <div className="space-y-2">
                    <Label>Jaar</Label>
                    <Select
                      value={String(formData.year)}
                      onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {[currentDate.getFullYear() - 2, currentDate.getFullYear() - 1, currentDate.getFullYear(), currentDate.getFullYear() + 1].map(y => (
                          <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Omzet (€) {formData.entry_type !== 'dag' && <span className="text-[#57534E] text-xs">- totaal voor de {formData.entry_type}</span>}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    placeholder="0.00"
                    required
                    data-testid="revenue-amount-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Aantal gasten {formData.entry_type !== 'dag' && <span className="text-[#57534E] text-xs">(totaal)</span>}</Label>
                    <Input
                      type="number"
                      value={formData.guests}
                      onChange={(e) => setFormData({ ...formData, guests: e.target.value })}
                      placeholder="0"
                      data-testid="revenue-guests-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Transacties {formData.entry_type !== 'dag' && <span className="text-[#57534E] text-xs">(totaal)</span>}</Label>
                    <Input
                      type="number"
                      value={formData.transactions}
                      onChange={(e) => setFormData({ ...formData, transactions: e.target.value })}
                      placeholder="0"
                      data-testid="revenue-transactions-input"
                    />
                  </div>
                </div>
                
                {/* Only show channel/location for daily entries */}
                {formData.entry_type === 'dag' && (
                  <>
                    <div className="space-y-2">
                      <Label>Kanaal (optioneel)</Label>
                      <Select
                        value={formData.channel || "none"}
                        onValueChange={(value) => setFormData({ ...formData, channel: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="revenue-channel-select">
                          <SelectValue placeholder="Selecteer kanaal" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Geen</SelectItem>
                          {channels.map(ch => (
                            <SelectItem key={ch.id} value={ch.name}>{ch.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Locatie (optioneel)</Label>
                      <Select
                        value={formData.location_id || "none"}
                        onValueChange={(value) => setFormData({ ...formData, location_id: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="revenue-location-select">
                          <SelectValue placeholder="Selecteer locatie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Geen</SelectItem>
                          {locations.map(loc => (
                            <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Dagdeel (optioneel)</Label>
                      <Select
                        value={formData.period_part || "none"}
                        onValueChange={(value) => setFormData({ ...formData, period_part: value === "none" ? "" : value })}
                      >
                        <SelectTrigger data-testid="revenue-daypart-select">
                          <SelectValue placeholder="Selecteer dagdeel" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Geen</SelectItem>
                          <SelectItem value="ochtend">Ochtend</SelectItem>
                          <SelectItem value="middag">Middag</SelectItem>
                          <SelectItem value="avond">Avond</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <Button type="submit" className="w-full btn-primary" data-testid="revenue-submit-btn">
                  {formData.entry_type === 'dag' ? 'Opslaan' : `${formData.entry_type.charAt(0).toUpperCase() + formData.entry_type.slice(1)}omzet opslaan`}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card className="card-base">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <Filter className="w-5 h-5 text-[#57534E]" />
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.startDate, 'dd MMM yyyy', { locale: nl })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.startDate}
                    onSelect={(date) => date && setFilters({ ...filters, startDate: date })}
                    locale={nl}
                  />
                </PopoverContent>
              </Popover>
              <span className="text-[#57534E]">tot</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-[140px] justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {format(filters.endDate, 'dd MMM yyyy', { locale: nl })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={filters.endDate}
                    onSelect={(date) => date && setFilters({ ...filters, endDate: date })}
                    locale={nl}
                  />
                </PopoverContent>
              </Popover>
            </div>
            <Select
              value={filters.location}
              onValueChange={(value) => setFilters({ ...filters, location: value })}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Alle locaties" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle locaties</SelectItem>
                {locations.map(loc => (
                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <FileText className="w-4 h-4 mr-2" />
                PDF
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportExcel} data-testid="export-excel-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Excel
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Period Revenue Overview - Year, Month, Week */}
      <Card className="card-base">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
            <TrendingUp className="w-5 h-5 text-[#C2410C]" />
            Omzet per Periode
          </CardTitle>
          <p className="text-sm text-[#57534E]">
            Selecteer jaar, maand en week om de omzet te bekijken • Klik op <Target className="w-3 h-3 inline" /> om doelen in te stellen
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Year Revenue */}
            <div className="bg-gradient-to-br from-[#C2410C]/5 to-[#C2410C]/10 rounded-xl p-5 border border-[#C2410C]/20">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-[#57534E]">Jaar Omzet</span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 hover:bg-[#C2410C]/10"
                    onClick={() => openQuickAdd('jaar')}
                    title="Jaaromzet toevoegen"
                    data-testid="quick-add-year"
                  >
                    <Plus className="w-4 h-4 text-[#C2410C]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => openTargetDialog('year')}
                    title="Jaardoel instellen"
                  >
                    <Target className="w-4 h-4 text-[#C2410C]" />
                  </Button>
                  <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                    <SelectTrigger className="w-[100px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(periodStats?.availableYears || [currentDate.getFullYear(), currentDate.getFullYear() - 1]).map(y => (
                        <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-3xl font-bold text-[#C2410C]">
                {formatCurrency(periodStats?.year?.current?.total || 0)}
              </p>
              
              {/* Target Progress */}
              {periodStats?.year?.target && (
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#57534E]">Doel: {formatCurrency(periodStats.year.target)}</span>
                    <span className="font-medium text-[#C2410C]">{periodStats.year.progress}%</span>
                  </div>
                  <Progress value={periodStats.year.progress} className="h-2" />
                  <p className="text-xs text-[#78716C]">
                    Nog {formatCurrency(Math.max(0, periodStats.year.target - (periodStats.year.current?.total || 0)))} te gaan
                  </p>
                </div>
              )}
              
              <div className="flex items-center gap-2 mt-2">
                {periodStats?.year?.change !== undefined && (
                  <span className={`flex items-center text-sm ${periodStats.year.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {periodStats.year.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {Math.abs(periodStats.year.change)}% vs {selectedYear - 1}
                  </span>
                )}
              </div>
              <p className="text-xs text-[#78716C] mt-1">
                {periodStats?.year?.current?.count || 0} transacties
              </p>
            </div>

            {/* Month Revenue */}
            <div className="bg-gradient-to-br from-[#1A2E05]/5 to-[#1A2E05]/10 rounded-xl p-5 border border-[#1A2E05]/20">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-[#57534E]">Maand Omzet</span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 hover:bg-[#1A2E05]/10"
                    onClick={() => openQuickAdd('maand')}
                    title="Maandomzet toevoegen"
                    data-testid="quick-add-month"
                  >
                    <Plus className="w-4 h-4 text-[#1A2E05]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => openTargetDialog('month')}
                    title="Maanddoel instellen"
                  >
                    <Target className="w-4 h-4 text-[#1A2E05]" />
                  </Button>
                  <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
                    <SelectTrigger className="w-[120px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {monthNames.map((name, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-3xl font-bold text-[#1A2E05]">
                {formatCurrency(periodStats?.month?.current?.total || 0)}
              </p>
              
              {/* Target Progress */}
              {periodStats?.month?.target && (
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#57534E]">Doel: {formatCurrency(periodStats.month.target)}</span>
                    <span className="font-medium text-[#1A2E05]">{periodStats.month.progress}%</span>
                  </div>
                  <Progress value={periodStats.month.progress} className="h-2 [&>div]:bg-[#1A2E05]" />
                  <p className="text-xs text-[#78716C]">
                    Nog {formatCurrency(Math.max(0, periodStats.month.target - (periodStats.month.current?.total || 0)))} te gaan
                  </p>
                </div>
              )}
              
              <div className="flex items-center gap-2 mt-2">
                {periodStats?.month?.change !== undefined && (
                  <span className={`flex items-center text-sm ${periodStats.month.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {periodStats.month.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {Math.abs(periodStats.month.change)}% vs vorige maand
                  </span>
                )}
              </div>
              <p className="text-xs text-[#78716C] mt-1">
                {monthNames[selectedMonth - 1]} {selectedYear} • {periodStats?.month?.current?.count || 0} transacties
              </p>
            </div>

            {/* Week Revenue */}
            <div className="bg-gradient-to-br from-[#D97706]/5 to-[#D97706]/10 rounded-xl p-5 border border-[#D97706]/20">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-[#57534E]">Week Omzet</span>
                <div className="flex items-center gap-1">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0 hover:bg-[#D97706]/10"
                    onClick={() => openQuickAdd('week')}
                    title="Weekomzet toevoegen"
                    data-testid="quick-add-week"
                  >
                    <Plus className="w-4 h-4 text-[#D97706]" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="h-8 w-8 p-0"
                    onClick={() => openTargetDialog('week')}
                    title="Weekdoel instellen"
                  >
                    <Target className="w-4 h-4 text-[#D97706]" />
                  </Button>
                  <Select value={String(selectedWeek)} onValueChange={(v) => setSelectedWeek(Number(v))}>
                    <SelectTrigger className="w-[100px] h-8 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 52 }, (_, i) => i + 1).map(w => (
                        <SelectItem key={w} value={String(w)}>Week {w}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-3xl font-bold text-[#D97706]">
                {formatCurrency(periodStats?.week?.current?.total || 0)}
              </p>
              
              {/* Target Progress */}
              {periodStats?.week?.target && (
                <div className="mt-3 space-y-1">
                  <div className="flex justify-between text-xs">
                    <span className="text-[#57534E]">Doel: {formatCurrency(periodStats.week.target)}</span>
                    <span className="font-medium text-[#D97706]">{periodStats.week.progress}%</span>
                  </div>
                  <Progress value={periodStats.week.progress} className="h-2 [&>div]:bg-[#D97706]" />
                  <p className="text-xs text-[#78716C]">
                    Nog {formatCurrency(Math.max(0, periodStats.week.target - (periodStats.week.current?.total || 0)))} te gaan
                  </p>
                </div>
              )}
              
              <div className="flex items-center gap-2 mt-2">
                {periodStats?.week?.change !== undefined && (
                  <span className={`flex items-center text-sm ${periodStats.week.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {periodStats.week.change >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {Math.abs(periodStats.week.change)}% vs vorige week
                  </span>
                )}
              </div>
              <p className="text-xs text-[#78716C] mt-1">
                Week {selectedWeek}, {selectedYear} • {periodStats?.week?.current?.count || 0} transacties
              </p>
            </div>
          </div>

          {/* Total All Time */}
          {periodStats?.totalAllTime > 0 && (
            <div className="mt-4 pt-4 border-t border-stone-200 flex items-center justify-between">
              <span className="text-sm text-[#57534E]">Totale omzet (alle tijd)</span>
              <span className="text-lg font-bold text-[#1C1917]">{formatCurrency(periodStats.totalAllTime)}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Target Setting Dialog */}
      <Dialog open={targetDialogOpen} onOpenChange={setTargetDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-[#C2410C]" />
              Omzet Doelstelling Instellen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-stone-50 rounded-lg">
              <p className="text-sm text-[#57534E]">
                {targetType === 'year' && `Jaardoel voor ${selectedYear}`}
                {targetType === 'month' && `Maanddoel voor ${monthNames[selectedMonth - 1]} ${selectedYear}`}
                {targetType === 'week' && `Weekdoel voor Week ${selectedWeek}, ${selectedYear}`}
              </p>
              <p className="text-xs text-[#78716C] mt-1">
                Dit doel wordt automatisch gekoppeld aan "Doelen & Structuur"
              </p>
            </div>
            
            <div className="space-y-2">
              <Label>Doel Bedrag (€)</Label>
              <Input
                type="number"
                value={targetAmount}
                onChange={(e) => setTargetAmount(e.target.value)}
                placeholder="bijv. 10000"
                className="text-lg"
              />
            </div>
            
            {periodStats?.[targetType]?.current?.total > 0 && (
              <div className="text-sm text-[#57534E]">
                Huidige omzet: <span className="font-bold text-[#C2410C]">
                  {formatCurrency(periodStats[targetType].current.total)}
                </span>
              </div>
            )}
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setTargetDialogOpen(false)}
              >
                Annuleren
              </Button>
              <Button 
                className="flex-1 btn-primary"
                onClick={handleSetTarget}
              >
                Opslaan
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* KPI Tiles - Current Filter Period */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="kpi-tile" data-testid="kpi-total-revenue">
          <p className="kpi-label">Gefilterde Omzet</p>
          <p className="kpi-value">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-[#57534E] mt-1">
            {format(filters.startDate, 'dd MMM', { locale: nl })} - {format(filters.endDate, 'dd MMM', { locale: nl })}
          </p>
        </div>
        <div className="kpi-tile" data-testid="kpi-avg-spending">
          <p className="kpi-label">Gem. Besteding</p>
          <p className="kpi-value">{formatCurrency(avgSpending)}</p>
          <p className="text-sm text-[#57534E] mt-1">per gast</p>
        </div>
        <div className="kpi-tile" data-testid="kpi-total-guests">
          <p className="kpi-label">Aantal Gasten</p>
          <p className="kpi-value">{formatNumber(totalGuests)}</p>
          <p className="text-sm text-[#57534E] mt-1">totaal</p>
        </div>
        <div className="kpi-tile" data-testid="kpi-transactions">
          <p className="kpi-label">Transacties</p>
          <p className="kpi-value">{formatNumber(totalTransactions)}</p>
          <p className="text-sm text-[#57534E] mt-1">totaal</p>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 card-base">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet Verloop</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={chartData}>
                  <defs>
                    <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#C2410C" stopOpacity={0.2}/>
                      <stop offset="95%" stopColor="#C2410C" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: '#57534E' }} />
                  <YAxis tick={{ fontSize: 12, fill: '#57534E' }} tickFormatter={(v) => `€${v}`} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'omzet' ? formatCurrency(value) : value,
                      name === 'omzet' ? 'Omzet' : 'Gasten'
                    ]}
                  />
                  <Area type="monotone" dataKey="omzet" stroke="#C2410C" fill="url(#colorRevenue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardHeader>
            <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet per Kanaal</CardTitle>
          </CardHeader>
          <CardContent>
            {channelData.length > 0 ? (
              <div className="h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={channelData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {channelData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[#57534E]">
                Nog geen kanaal data beschikbaar
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Category Trend Analysis */}
      {categoryTrends.length > 0 && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <BarChart3 className="w-5 h-5 text-[#C2410C]" />
              Categorie Trend Analyse
            </CardTitle>
            <p className="text-sm text-[#57534E] mt-1">
              Prestaties per productcategorie vergelijking met vorige periode
              {trendsPeriod.previous && (
                <span className="ml-2 text-xs bg-stone-100 px-2 py-1 rounded">
                  vs {trendsPeriod.previous.start} t/m {trendsPeriod.previous.end}
                </span>
              )}
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Category Performance Cards */}
              <div className="space-y-3">
                <h4 className="font-semibold text-sm text-[#57534E] mb-4">Top Categorieën</h4>
                {categoryTrends.slice(0, 5).map((trend, index) => (
                  <div 
                    key={trend.category} 
                    className="flex items-center justify-between p-4 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <div>
                        <p className="font-medium text-[#1C1917]">{trend.category}</p>
                        <p className="text-xs text-[#57534E]">
                          {trend.entries} transactie(s)
                          {trend.prevAmount > 0 && (
                            <span className="ml-2 text-stone-400">
                              (was {formatCurrency(trend.prevAmount)})
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-[#C2410C]">{formatCurrency(trend.currentAmount)}</p>
                      <div className={`flex items-center justify-end gap-1 text-xs ${
                        trend.trend === 'up' ? 'text-emerald-600' : 'text-rose-600'
                      }`}>
                        {trend.trend === 'up' ? (
                          <ArrowUpRight className="w-3 h-3" />
                        ) : (
                          <ArrowDownRight className="w-3 h-3" />
                        )}
                        <span>{Math.abs(trend.change)}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Category Bar Chart */}
              <div>
                <h4 className="font-semibold text-sm text-[#57534E] mb-4">Omzet per Categorie</h4>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart 
                      data={categoryTrends.slice(0, 6)} 
                      layout="vertical"
                      margin={{ left: 20, right: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" horizontal={true} vertical={false} />
                      <XAxis 
                        type="number" 
                        tickFormatter={(v) => `€${(v/1000).toFixed(0)}k`}
                        stroke="#57534E"
                        fontSize={12}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="category" 
                        width={100}
                        stroke="#57534E"
                        fontSize={11}
                        tickFormatter={(v) => v.length > 12 ? v.slice(0, 12) + '...' : v}
                      />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        labelFormatter={(label) => label}
                        contentStyle={{ 
                          backgroundColor: '#FAFAF9', 
                          border: '1px solid #E7E5E4',
                          borderRadius: '8px'
                        }}
                      />
                      <Bar 
                        dataKey="currentAmount" 
                        fill="#C2410C" 
                        radius={[0, 4, 4, 0]}
                        name="Omzet"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Category Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6 pt-6 border-t border-stone-200">
              <div className="text-center">
                <p className="text-sm text-[#57534E]">Totaal Categorieën</p>
                <p className="text-2xl font-bold text-[#1C1917]">{trendsSummary.totalCategories || categoryTrends.length}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#57534E]">Beste Categorie</p>
                <p className="text-lg font-bold text-[#C2410C]">
                  {trendsSummary.bestCategory || categoryTrends[0]?.category || '-'}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#57534E]">Hoogste Groei</p>
                <p className="text-lg font-bold text-emerald-600">
                  {trendsSummary.highestGrowth 
                    ? `${trendsSummary.highestGrowth.change >= 0 ? '+' : ''}${trendsSummary.highestGrowth.change}%`
                    : categoryTrends.length > 0 
                      ? `+${Math.max(...categoryTrends.map(t => parseFloat(t.change))).toFixed(1)}%`
                      : '-'}
                </p>
                {trendsSummary.highestGrowth && (
                  <p className="text-xs text-[#57534E]">{trendsSummary.highestGrowth.category}</p>
                )}
              </div>
              <div className="text-center">
                <p className="text-sm text-[#57534E]">Huidige Periode</p>
                <p className="text-lg font-bold text-[#1C1917]">
                  {formatCurrency(trendsSummary.totalCurrentRevenue || totalCategoryRevenue)}
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#57534E]">Vorige Periode</p>
                <p className="text-lg font-bold text-stone-500">
                  {formatCurrency(trendsSummary.totalPrevRevenue || 0)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Data Table */}
      <Card className="card-base">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>Omzet Overzicht</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="table-header">
                  <TableHead>Datum</TableHead>
                  <TableHead>Omzet</TableHead>
                  <TableHead>Gasten</TableHead>
                  <TableHead>Gem. Besteding</TableHead>
                  <TableHead>Transacties</TableHead>
                  <TableHead>Kanaal/Bron</TableHead>
                  <TableHead>Dagdeel</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-[#57534E]">
                      Nog geen omzet gegevens. Voeg uw eerste omzet toe.
                    </TableCell>
                  </TableRow>
                ) : (
                  entries
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((entry) => (
                      <TableRow key={entry.id} className={`table-row ${entry.source === 'pdf_upload' ? 'bg-orange-50/50' : ''}`}>
                        <TableCell className="font-medium">
                          <div>
                            {formatDate(entry.date)}
                            {entry.period_end && entry.period_end !== entry.date && (
                              <span className="text-xs text-[#57534E] block">
                                t/m {formatDate(entry.period_end)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="font-semibold text-[#C2410C]">
                          <div>
                            {formatCurrency(entry.amount)}
                            {entry.total_excl_btw && (
                              <span className="text-xs text-[#57534E] block">
                                excl. BTW: {formatCurrency(entry.total_excl_btw)}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>{entry.guests || '-'}</TableCell>
                        <TableCell>
                          {entry.guests > 0 
                            ? formatCurrency(entry.amount / entry.guests) 
                            : '-'}
                        </TableCell>
                        <TableCell>{entry.transactions || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {entry.source === 'pdf_upload' ? (
                              <Badge className="bg-[#C2410C]/10 text-[#C2410C] text-xs">
                                <FileText className="w-3 h-3 mr-1" />
                                PDF Import
                              </Badge>
                            ) : (
                              entry.channel || '-'
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="capitalize">{entry.period_part || '-'}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-rose-600 hover:text-rose-700"
                            onClick={() => handleDelete(entry.id)}
                          >
                            Verwijder
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Kassa Rapporten Section */}
      {kassaReports.length > 0 && (
        <Card className="card-base">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#C2410C]" />
              Geüploade Kassa Rapporten
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kassaReports.map(report => (
                <Card key={report.id} className="card-base hover:shadow-md transition-shadow cursor-pointer" onClick={() => { setSelectedReport(report); setReportDialogOpen(true); }}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-semibold text-sm">{report.filename}</p>
                        <p className="text-xs text-[#57534E] mt-1">
                          {report.period_start} t/m {report.period_end}
                        </p>
                        <p className="text-lg font-bold text-[#C2410C] mt-2">
                          {formatCurrency(report.total_incl_btw)}
                        </p>
                      </div>
                      <Button variant="ghost" size="icon" className="text-rose-500" onClick={(e) => { e.stopPropagation(); handleDeleteReport(report.id); }}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Event Omzet Section */}
      {eventRevenue.length > 0 && (
        <Card className="card-base border-2 border-purple-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <PartyPopper className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Event Omzet</CardTitle>
                  <p className="text-sm text-[#57534E]">Omzet gekoppeld aan events uit Marketing & Acties</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalEventRevenue)}</p>
                <p className="text-sm text-[#57534E]">{totalEventGuests} gasten</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Omzet per Event */}
            {Object.keys(eventRevenueByEvent).length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Omzet per Event</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(eventRevenueByEvent)
                    .sort((a, b) => b[1].amount - a[1].amount)
                    .map(([eventName, data]) => (
                      <div key={eventName} className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="flex items-center gap-2 mb-2">
                          <PartyPopper className="w-4 h-4 text-purple-600" />
                          <p className="font-medium text-[#1C1917] truncate">{eventName}</p>
                        </div>
                        <p className="text-xl font-bold text-purple-600">{formatCurrency(data.amount)}</p>
                        <p className="text-xs text-[#57534E]">{data.guests} gasten • {data.items.length} omzet item(s)</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Alle Event Omzet */}
            <div>
              <h4 className="font-semibold text-[#1C1917] mb-3">Alle Event Omzet ({eventRevenue.length})</h4>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Gasten</TableHead>
                    <TableHead className="text-right">Bedrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eventRevenue
                    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                    .map((rev) => (
                      <TableRow key={rev.id} className="table-row">
                        <TableCell>{formatDate(rev.date)}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                            <PartyPopper className="w-3 h-3" />
                            {rev.event_name}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{rev.description}</TableCell>
                        <TableCell className="capitalize">{rev.category}</TableCell>
                        <TableCell className="text-right">{rev.guests || 0}</TableCell>
                        <TableCell className="text-right font-bold text-purple-600">
                          {formatCurrency(rev.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Arrangement Omzet Section */}
      {arrangementRevenue.length > 0 && (
        <Card className="card-base border-2 border-amber-200">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Package className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Arrangement Omzet</CardTitle>
                  <p className="text-sm text-[#57534E]">Omzet gekoppeld aan arrangementen uit Marketing & Acties</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(totalArrangementRevenue)}</p>
                <p className="text-sm text-[#57534E]">{totalArrangementGuests} gasten</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Omzet per Arrangement */}
            {Object.keys(arrangementRevenueByArr).length > 0 && (
              <div>
                <h4 className="font-semibold text-[#1C1917] mb-3">Omzet per Arrangement</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {Object.entries(arrangementRevenueByArr)
                    .sort((a, b) => b[1].amount - a[1].amount)
                    .map(([arrName, data]) => (
                      <div key={arrName} className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                        <div className="flex items-center gap-2 mb-2">
                          <Package className="w-4 h-4 text-amber-600" />
                          <p className="font-medium text-[#1C1917] truncate">{arrName}</p>
                        </div>
                        <p className="text-xl font-bold text-amber-600">{formatCurrency(data.amount)}</p>
                        <p className="text-xs text-[#57534E]">{data.guests} gasten • {data.items.length} omzet item(s)</p>
                      </div>
                    ))}
                </div>
              </div>
            )}

            {/* Alle Arrangement Omzet */}
            <div>
              <h4 className="font-semibold text-[#1C1917] mb-3">Alle Arrangement Omzet ({arrangementRevenue.length})</h4>
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead>Datum</TableHead>
                    <TableHead>Arrangement</TableHead>
                    <TableHead>Omschrijving</TableHead>
                    <TableHead>Categorie</TableHead>
                    <TableHead className="text-right">Gasten</TableHead>
                    <TableHead className="text-right">Bedrag</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {arrangementRevenue
                    .sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))
                    .map((rev) => (
                      <TableRow key={rev.id} className="table-row">
                        <TableCell>{formatDate(rev.date)}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                            <Package className="w-3 h-3" />
                            {rev.arrangement_name}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium">{rev.description}</TableCell>
                        <TableCell className="capitalize">{rev.category}</TableCell>
                        <TableCell className="text-right">{rev.guests || 0}</TableCell>
                        <TableCell className="text-right font-bold text-amber-600">
                          {formatCurrency(rev.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Report Detail Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-[#C2410C]" />
              Kassa Rapport Details
            </DialogTitle>
          </DialogHeader>
          {selectedReport && (
            <div className="space-y-6 pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#57534E]">Periode</p>
                  <p className="font-semibold">{selectedReport.period_start} t/m {selectedReport.period_end}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-[#57534E]">Bestand</p>
                  <p className="font-semibold">{selectedReport.filename}</p>
                </div>
              </div>

              {/* Totalen */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-[#57534E]">Totaal excl. BTW</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedReport.total_excl_btw)}</p>
                </div>
                <div className="bg-[#C2410C]/10 rounded-lg p-4 text-center">
                  <p className="text-sm text-[#57534E]">Totaal incl. BTW</p>
                  <p className="text-xl font-bold text-[#C2410C]">{formatCurrency(selectedReport.total_incl_btw)}</p>
                </div>
                <div className="bg-stone-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-[#57534E]">BTW Bedrag</p>
                  <p className="text-xl font-bold">{formatCurrency(selectedReport.btw_amount)}</p>
                </div>
              </div>

              {/* Betaalwijzes */}
              {selectedReport.payment_methods?.length > 0 && (
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <CreditCard className="w-4 h-4" />
                    Betaalwijzes
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedReport.payment_methods.map((pm, i) => (
                      <div key={i} className="flex items-center justify-between bg-stone-50 rounded-lg p-3">
                        <span className="flex items-center gap-2">
                          {pm.name.toLowerCase().includes('pin') ? <CreditCard className="w-4 h-4 text-blue-500" /> : <Wallet className="w-4 h-4 text-emerald-500" />}
                          {pm.name}
                        </span>
                        <span className="font-semibold">{formatCurrency(pm.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Categorieën */}
              {selectedReport.categories?.length > 0 && (
                <div>
                  <h4 className="font-semibold flex items-center gap-2 mb-3">
                    <Receipt className="w-4 h-4" />
                    Omzet per Categorie
                  </h4>
                  <Table>
                    <TableHeader>
                      <TableRow className="table-header">
                        <TableHead>Categorie</TableHead>
                        <TableHead className="text-right">Excl. BTW</TableHead>
                        <TableHead className="text-right">Incl. BTW</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedReport.categories.map((cat, i) => (
                        <TableRow key={i} className="table-row">
                          <TableCell className="font-medium">{cat.name}</TableCell>
                          <TableCell className="text-right">{formatCurrency(cat.amount_excl)}</TableCell>
                          <TableCell className="text-right font-semibold">{formatCurrency(cat.amount_incl)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              <div className="flex justify-end pt-4">
                <DialogClose asChild>
                  <Button variant="outline">Sluiten</Button>
                </DialogClose>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* AI Key Required Dialog */}
      <AIKeyRequiredDialog 
        open={showAIKeyDialog} 
        onOpenChange={setShowAIKeyDialog} 
      />
    </div>
  );
};

export default OmzetVerkoopPage;
